import * as React from "react";
import {
    ArrowSync20Regular,
    ChevronDown20Regular,
    ChevronRight20Regular,
    Edit20Regular,
    Folder20Regular
} from "@fluentui/react-icons";
import type { FluentIcon } from "@fluentui/react-icons";
import { ITreeNodeData, TreeNodeIconKey } from "../types/TreeNodeData";
import { If } from "../../core/components/If";

const TREE_NODE_ICONS: Record<TreeNodeIconKey, FluentIcon> = {
    folder: Folder20Regular
};

interface ITreeNodeProps<T> {
    nodeData: ITreeNodeData<T>;
    selectedNodeChanged: (nodeData: ITreeNodeData<T>) => void;
    isEditing: boolean;
    addSourceButtonText: string;
    deleteSourceButtonText: string;
    addSource: (parent: T) => void;
    deleteSource: (source: T) => void;
    alwaysExpanded: boolean;
    indentation: number;
    deleteRoleDisabled?: boolean;
    showIcons?: boolean;
}

export class TreeNode<T> extends React.Component<ITreeNodeProps<T>> {
    constructor(props: ITreeNodeProps<T>) {
        super(props);
    }

    onClick(
        event: React.MouseEvent<HTMLLIElement, MouseEvent>
    ) {
        event.stopPropagation();
        this.props.selectedNodeChanged(this.props.nodeData);
    }

    addSourceClick() {
        this.props.addSource(this.props.nodeData.object);
    }

    deleteSourceClick() {
        this.props.deleteSource(this.props.nodeData.object);
    }

    renderChevronIcon(
        key: number,
        onClick?: React.MouseEventHandler<any>,
        style?: React.CSSProperties
    ): JSX.Element {
        const { alwaysExpanded, nodeData } = this.props;
        const hasChildren = nodeData.hasChildren ??
            (nodeData.children != null && nodeData.children.length > 0);
        const className = "icon expand-icon chevron";

        if (!hasChildren) {
            return <span key={key} className={className} onClick={onClick} style={style} />;
        }

        if (alwaysExpanded) {
            return <span key={key} onClick={onClick} style={style} />;
        }

        if (nodeData.isExpanded && nodeData.children?.length) {
            return <ChevronDown20Regular key={key} className={className} aria-hidden="true" onClick={onClick} style={style} />;
        }

        return <ChevronRight20Regular key={key} className={className} aria-hidden="true" onClick={onClick} style={style} />;
    }

    renderChild(node: ITreeNodeData<T>, index: number): JSX.Element {
        const props = this.props;
        return <TreeNode
            key={index}
            nodeData={node}
            selectedNodeChanged={props.selectedNodeChanged}
            isEditing={props.isEditing}
            addSourceButtonText={props.addSourceButtonText}
            deleteSourceButtonText={props.deleteSourceButtonText}
            addSource={props.addSource}
            deleteSource={props.deleteSource}
            alwaysExpanded={props.alwaysExpanded}
            indentation={props.indentation + 1}
            deleteRoleDisabled={node.isDeleteDisabled}
            showIcons={props.showIcons}
        />
    }

    renderChildren(): JSX.Element[] {
        const props = this.props;
        const data: ITreeNodeData<T> = props.nodeData;
        const isExpanded: boolean = props.alwaysExpanded || data.isExpanded;

        if (isExpanded && data.children && data.children.length > 0) {
            return data.children.map((node, index) => this.renderChild(node, index));
        }
        else {
            return null;
        }
    }

    renderListItemParts(): JSX.Element[] {
        const data: ITreeNodeData<T> = this.props.nodeData;
        const spans: JSX.Element[] = [];
        let i = 0;
        for (i = 0; i < this.props.indentation; i++) {
            spans.push(<span key={i} className="indent"></span>)
        }

        spans.push(this.renderChevronIcon(i++));
        const NodeIcon = data.icon
            ? TREE_NODE_ICONS[data.icon]
            : undefined;

        if (this.props.showIcons && NodeIcon) {
            spans.push(<NodeIcon key={i++} className="icon node-icon" aria-hidden="true" />);
        }
        spans.push(<span key={i++} className="text">{data.name}</span>);

        const count = this.props.nodeData.progressCount;
        if (count) {
            spans.push(<span key={i++} className="badge"><ArrowSync20Regular className="icon syncOccurrenceIcon" aria-hidden="true" />{count}</span>);
        }

        return spans;
    }

    generateRandomID(): string{
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
          });
    }

    renderEditHierarchyButtons(): JSX.Element[] {
        const data: ITreeNodeData<T> = this.props.nodeData;
        const spans: JSX.Element[] = [];
        let i = 0;
        const uuId = this.generateRandomID();
        // Add indent span if there is any props.indentation
        for (i = 0; i < this.props.indentation; i++) {
            spans.push(<span key={i} className="indent"></span>)
        }
        spans.push(this.renderChevronIcon(i++, this.onClick.bind(this), { cursor: 'pointer' }));

        spans.push(
            <span key={i} className="indent">
                <a
                    href={"#editHierarchy" + data.level + this.props.nodeData.id  + uuId}
                    data-toggle="collapse"
                ><Edit20Regular className="icon editIcon" aria-hidden="true" /> {data.name}
                </a>
                <div
                    id={"editHierarchy" + data.level + this.props.nodeData.id + uuId}
                    className="collapse editBox indent"
                >
                    <ul className="indent">
                        <If condition={data.level < 2}>
                            <li>
                                <a onClick={this.addSourceClick.bind(this)}>
                                    {this.props.addSourceButtonText}
                                </a>
                            </li>
                        </If>
                        <If condition={data.level > 0}>
                            <li>
                                <a
                                    className={`deleteText ${this.props.deleteRoleDisabled ? 'disabled' : ''}`}
                                    onClick={!this.props.deleteRoleDisabled ? this.deleteSourceClick.bind(this) : undefined}
                                    style={{ pointerEvents: this.props.deleteRoleDisabled ? 'none' : 'auto', opacity: this.props.deleteRoleDisabled ? 0.5 : 1 }}
                                >
                                    {this.props.deleteSourceButtonText}
                                </a>
                            </li>
                        </If>
                    </ul>
                </div>
            </span>)
        return spans;
    }

    render() {
        const props = this.props;
        if (!props.nodeData.visible) {
            return null;
        }
        let className = "list-group-item node-treeview";
        const style: React.CSSProperties = { color: undefined, backgroundColor: undefined, border: 'none' };

        let editingClassName = "list-group-item node-treeview";
        const editingStyle: React.CSSProperties = { backgroundColor: undefined, border: 'none' };

        if (this.props.nodeData.active && !props.isEditing) {
            className = className + " node-selected";
            style.color = '#FFFFFF'
        }

        return (<>
            <If condition={!props.isEditing}>
                <li onClick={this.onClick.bind(this)} className={className} style={style}>
                    {this.renderListItemParts()}
                </li>
            </If>

            <If condition={props.isEditing}>
                <li className={editingClassName} style={editingStyle}>
                    {/* Edit Source Hierarchy*/}
                    {this.renderEditHierarchyButtons()}
                </li>
            </If>



            {/* Children */}
            {this.renderChildren()}
        </>

        );
    }
}
